import { __awaiter } from "tslib";
import { Session } from '@inrupt/solid-client-authn-browser';
import { SolidProfileObject } from '../common';
import { SolidService } from '../common/SolidService';
import { buildAuthenticatedFetch, loadOidcContextFromStorage, maybeBuildRpInitiatedLogout, } from '@inrupt/solid-client-authn-core';
import { getTokens } from '@inrupt/oidc-client-ext';
import IssuerConfigFetcher from './IssuerConfigFetcher';
export class SolidClientService extends SolidService {
    constructor(options) {
        var _a;
        super(options);
        this.options.autoLogin = (_a = this.options.autoLogin) !== null && _a !== void 0 ? _a : false;
        this.once('build', this._initialize.bind(this));
    }
    get session() {
        return this._session;
    }
    set session(value) {
        this._session = value;
    }
    _initialize() {
        return new Promise((resolve) => {
            this.issuerConfigFetcher = new IssuerConfigFetcher(this.storageUtility);
            // Default session (local storage)
            this.handleLogin()
                .then(() => {
                resolve();
            })
                .catch((err) => {
                this.emit('error', err);
                resolve(); // A login error should not break the build process
            })
                .finally(() => {
                this.emitAsync('ready');
            });
        });
    }
    logout(session) {
        return new Promise((resolve, reject) => {
            session
                .logout()
                .then(() => {
                this.session = undefined;
                return this.storage.delete('currentSession');
            })
                .then(() => {
                resolve();
            })
                .catch(reject);
        });
    }
    /**
     * Login a Solid browser user
     * @param {string} oidcIssuer OpenID Issuer
     * @param {boolean} remember Remember the session
     * @returns {Promise<void>} Session promise
     */
    login(oidcIssuer = this.options.defaultOidcIssuer, remember = false) {
        return new Promise((resolve, reject) => {
            const session = this.createSession({
                insecureStorage: this.storage,
                secureStorage: this.storage,
            });
            // Set the current session or at least override it
            this.storage
                .set('currentSession', session.info.sessionId)
                .then(() => {
                // Create handle redirect function
                const handleRedirect = this.options.handleRedirect
                    ? (url) => {
                        const errorCallback = (error) => {
                            if (error) {
                                // Check if promise is already resolved, else reject
                                if (session.info.isLoggedIn) {
                                    this.emit('error', error);
                                }
                                else {
                                    reject(error);
                                    return;
                                }
                            }
                            resolve();
                        };
                        return this.options.handleRedirect(url, errorCallback);
                    }
                    : undefined;
                return session.login({
                    oidcIssuer,
                    clientName: this.options.clientName,
                    clientId: this.options.clientId,
                    clientSecret: this.options.clientSecret,
                    redirectUrl: this.options.redirectUrl ? this.options.redirectUrl : window.location.href,
                    handleRedirect: handleRedirect,
                });
            })
                .then(() => {
                resolve();
            })
                .catch(reject);
        });
    }
    handleLogin() {
        return new Promise((resolve, reject) => {
            let storedSessionData = undefined;
            let session = undefined;
            let sessionId = undefined;
            // Get the current session if any
            this.storage
                .get('currentSession')
                .then((currentLocalSessionId) => {
                if (currentLocalSessionId) {
                    // Ugly workaround for https://github.com/inrupt/solid-client-authn-js/issues/2095
                    const CURRENT_SESSION_KEY = 'solidClientAuthn:currentSession';
                    const currentGlobalSessionId = window.localStorage.getItem(CURRENT_SESSION_KEY);
                    if (currentGlobalSessionId && currentLocalSessionId !== currentGlobalSessionId) {
                        window.localStorage.setItem(CURRENT_SESSION_KEY, currentLocalSessionId);
                    }
                }
                if (!currentLocalSessionId) {
                    resolve(undefined); // No user logged in so no error
                    return;
                }
                sessionId = currentLocalSessionId;
                // Check if we have some information stored about this session
                return this.findSessionInfoById(currentLocalSessionId);
            })
                .then((data) => __awaiter(this, void 0, void 0, function* () {
                storedSessionData = data;
                session = this.createSession({
                    sessionInfo: Object.assign({ sessionId }, storedSessionData),
                    insecureStorage: this.storage,
                    secureStorage: this.storage,
                });
                return this.handleRedirect(session);
            }))
                .then((sessionInfo) => __awaiter(this, void 0, void 0, function* () {
                if (sessionInfo && sessionInfo.isLoggedIn) {
                    this.session = session;
                    console.log(sessionInfo, session);
                    yield this.storage.set('currentSession', sessionInfo.sessionId);
                    const object = new SolidProfileObject(sessionInfo.webId);
                    object.sessionId = sessionInfo.sessionId;
                    return this.storeProfile(object);
                }
                else {
                    // Session is not logged in
                    yield this.storage.delete('currentSession');
                    reject(new Error(`Unable to log in to Solid Pod!`));
                }
            }))
                .then(() => {
                this.emit('login', this.session);
                resolve(this.session);
            })
                .catch(reject);
        });
    }
    handleRedirect(session) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const url = new URL(window.location.href);
                // Check if can process
                if (url.searchParams.get('code') === null && url.searchParams.get('state') === null) {
                    if (!session) {
                        return undefined;
                    }
                    // First check if tokens in memory
                    const tokensString = yield this.storage.get(`solidClientAuthenticationUser:${session.info.sessionId}:tokens`);
                    if (tokensString) {
                        const tokens = JSON.parse(tokensString);
                        const authFetch = yield buildAuthenticatedFetch(fetch, tokens.accessToken, {
                            dpopKey: tokens.dpopKey,
                            refreshOptions: undefined,
                            eventEmitter: undefined,
                            expiresIn: tokens.expiresIn,
                        });
                        const sessionInfo = yield this.findSessionInfoById(session.info.sessionId);
                        if (!sessionInfo) {
                            throw new Error(`Could not retrieve session: [${session.info.sessionId}].`);
                        }
                        const { issuerConfig } = yield loadOidcContextFromStorage(session.info.sessionId, this.storageUtility, this.issuerConfigFetcher);
                        Object.assign(sessionInfo, {
                            fetch: authFetch,
                            getLogoutUrl: maybeBuildRpInitiatedLogout({
                                idTokenHint: tokens.idToken,
                                endSessionEndpoint: issuerConfig.endSessionEndpoint,
                            }),
                            expirationDate: tokens.expirationDate,
                        });
                        return Object.assign(session.info, sessionInfo);
                    }
                    return session.handleIncomingRedirect(url.href);
                }
                // Get OAuth state
                const oauthState = url.searchParams.get('state');
                const storedSessionId = (yield this.storageUtility.getForUser(oauthState, 'sessionId', {
                    errorIfNull: true,
                }));
                // Get stored data for session
                const { issuerConfig, codeVerifier, redirectUrl: storedRedirectIri, dpop: isDpop, } = yield loadOidcContextFromStorage(storedSessionId, this.storageUtility, this.issuerConfigFetcher);
                const iss = url.searchParams.get('iss');
                if (typeof iss === 'string' && iss !== issuerConfig.issuer) {
                    throw new Error(`The value of the iss parameter (${iss}) does not match the issuer identifier of the authorization server (${issuerConfig.issuer}). See [rfc9207](https://www.rfc-editor.org/rfc/rfc9207.html#section-2.3-3.1.1)`);
                }
                if (codeVerifier === undefined) {
                    throw new Error(`The code verifier for session ${storedSessionId} is missing from storage.`);
                }
                if (storedRedirectIri === undefined) {
                    throw new Error(`The redirect URL for session ${storedSessionId} is missing from storage.`);
                }
                const client = yield this.clientRegistrar.getClient({ sessionId: storedSessionId }, issuerConfig);
                const tokenCreatedAt = Date.now();
                const tokens = yield getTokens(issuerConfig, client, {
                    grantType: 'authorization_code',
                    code: url.searchParams.get('code'),
                    codeVerifier: codeVerifier,
                    redirectUrl: storedRedirectIri,
                }, isDpop);
                const expirationDate = typeof tokens.expiresIn === 'number' ? tokenCreatedAt + tokens.expiresIn * 1000 : undefined;
                yield this.storage.set(`solidClientAuthenticationUser:${storedSessionId}:tokens`, JSON.stringify(Object.assign(Object.assign({}, tokens), { expirationDate })));
                const authFetch = yield buildAuthenticatedFetch(fetch, tokens.accessToken, {
                    dpopKey: tokens.dpopKey,
                    refreshOptions: undefined,
                    eventEmitter: undefined,
                    expiresIn: tokens.expiresIn,
                });
                yield this.storageUtility.setForUser(storedSessionId, {
                    webId: tokens.webId,
                    isLoggedIn: 'true',
                }, { secure: true });
                const sessionInfo = yield this.findSessionInfoById(storedSessionId);
                if (!sessionInfo) {
                    throw new Error(`Could not retrieve session: [${storedSessionId}].`);
                }
                window.history.replaceState({}, document.title, storedRedirectIri);
                Object.assign(sessionInfo, {
                    fetch: authFetch,
                    getLogoutUrl: maybeBuildRpInitiatedLogout({
                        idTokenHint: tokens.idToken,
                        endSessionEndpoint: issuerConfig.endSessionEndpoint,
                    }),
                    expirationDate,
                });
                return Object.assign(session.info, sessionInfo);
            }
            catch (error) {
                this.emit('error', error);
                return undefined;
            }
        });
    }
    createSession(options) {
        return new Session(options);
    }
}
//# sourceMappingURL=SolidClientService.js.map